import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slideshow component
function Slideshow({ images, title }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const next = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

  if (!images || images.length === 0) return null;

  return (
    <div className="relative">
      <div className="flex items-center justify-center gap-2">
        <button onClick={prev} className="arrow-btn-2006 absolute left-0">
          <ChevronLeft size={16} />
        </button>
        <img
          src={images[currentIndex]}
          alt={`${title} - ${currentIndex + 1}`}
          className="max-h-[400px] object-contain border-2 border-[var(--border-color)]"
        />
        <button onClick={next} className="arrow-btn-2006 absolute right-0">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="text-center text-[10px] text-[var(--text-white)] opacity-60 mt-2">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
}

export default function PhotosPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const res = await fetch(`${API}/photos`);
      if (res.ok) {
        setPhotos(await res.json());
      }
    } catch (e) {
      console.error("Error fetching photos:", e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="page-wrapper" data-testid="photos-page">
      <div className="myspace-container">
        {/* Header */}
        <div className="myspace-header">
          <h3>kezzio's photos</h3>
        </div>

        <div className="myspace-section m-2">
          <div className="myspace-section-title">:: Photo Gallery ::</div>
          <div className="myspace-section-content">
            {loading ? (
              <div className="text-center text-[var(--text-white)] opacity-80 text-[11px] py-4">
                Loading photos...
              </div>
            ) : photos.length > 0 ? (
              <div className="space-y-6">
                {photos.map((photo) => (
                  <div key={photo.id} className="photo-post-2006" data-testid={`photo-post-${photo.id}`}>
                    {/* Post Header */}
                    <div className="flex justify-between items-center mb-2 pb-2 border-b border-[var(--border-color)]">
                      <span className="text-[var(--accent-orange)] font-bold text-[12px]">{photo.title}</span>
                      <span className="text-[9px] text-[var(--text-white)] opacity-50">{formatDate(photo.created_at)}</span>
                    </div>

                    {/* Post Content based on type */}
                    {photo.post_type === 'single' && photo.images[0] && (
                      <div className="text-center">
                        <img
                          src={photo.images[0]}
                          alt={photo.title}
                          className="max-w-full max-h-[500px] object-contain border-2 border-[var(--border-color)] mx-auto"
                        />
                      </div>
                    )}

                    {photo.post_type === 'collage' && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {photo.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`${photo.title} - ${idx + 1}`}
                            className="w-full aspect-square object-cover border-2 border-[var(--border-color)]"
                          />
                        ))}
                      </div>
                    )}

                    {photo.post_type === 'slideshow' && (
                      <Slideshow images={photo.images} title={photo.title} />
                    )}

                    {/* Description */}
                    {photo.description && (
                      <div className="text-[11px] text-[var(--text-white)] opacity-80 mt-3 pt-2 border-t border-[var(--border-color)]">
                        {photo.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-[var(--text-white)] opacity-80 text-[11px] py-8">
                No photos yet. Check back soon!
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-[10px] text-[var(--text-white)] opacity-50 py-3 border-t border-[var(--border-color)]">
          <a href="/" className="text-[#66ccff]">&laquo; Back to Home</a>
        </div>
      </div>
    </div>
  );
}
